import * as pdfjsLib from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker

/**
 * Converts a PDF file to an array of image files (one per page)
 * @param pdfFile - The PDF file to convert
 * @param maxPages - Maximum number of pages to convert (default: 9 for LinkedIn limit)
 * @returns Object with image files and total page count
 */
export async function convertPdfToImages(
  pdfFile: File,
  maxPages: number = 9,
): Promise<{ imageFiles: File[]; totalPages: number }> {
  try {
    const arrayBuffer = await pdfFile.arrayBuffer()

    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const totalPages = pdf.numPages
    const pagesToConvert = Math.min(totalPages, maxPages)

    const imageFiles: File[] = []

    for (let pageNum = 1; pageNum <= pagesToConvert; pageNum++) {
      const page = await pdf.getPage(pageNum)
      const viewport = page.getViewport({ scale: 2.0 })
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const context = canvas.getContext('2d')

      if (!context) {
        throw new Error('Failed to get canvas context')
      }

      await page.render({
        canvasContext: context,
        viewport: viewport,
        canvas: canvas,
      }).promise

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to convert canvas to blob'))
            }
          },
          'image/jpeg',
          0.9,
        )
      })

      const imageFile = new File(
        [blob],
        `${pdfFile.name.replace('.pdf', '')}-page-${pageNum}.jpg`,
        { type: 'image/jpeg' },
      )

      imageFiles.push(imageFile)
    }

    return { imageFiles, totalPages }
  } catch (error) {
    console.error('Error converting PDF to images:', error)
    throw new Error('Failed to convert PDF to images')
  }
}
